import axios from 'axios'

import EnvConstants, { isInternalEnv } from 'app/services/core/config.service'
import AuthApi from 'app/services/core/login.service'
import EntitlementService from 'app/services/core/entitlement.service'
import {
  ALL_GRADES,
  BETA_ASSET_KEY,
  GRADE_ASSETS,
  GRADE_PROGRAMS,
  HUB_ASSET_BUSINESS_KEY,
  HUB_ASSET_KEY,
  HUB_PROGRAMS,
  INTERACTIVE_CLASSROOM_NEW_FOR_BTS_22_ASSET_KEY,
} from 'app/constants/entitlement.constant'
import {
  collectAssetsFromEntitlements,
  collectAssetsFromUserPrograms,
} from 'app/utils/entitlement.utils'
import { Asset, UserProgram } from 'app/types/asset.types'
import { allowedSoundsAndSonidosGrades } from 'app/constants/home-content-buttons.constants'

export const getUserAssets = async () => {
  const token = AuthApi.getAccessToken()
  const userBk = AuthApi.getUserBk()
  const url = `${EnvConstants.RETAIL_URL}/api/licensing/users/${userBk}/assets`
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }

  const injectTestAssets = (userAssets: Asset[]) => {
    const params = new URLSearchParams(window.location.search)
    const hubProgramsFromUriStr = params.get('programs')

    if (hubProgramsFromUriStr) {
      Object.keys(GRADE_ASSETS).forEach((hubProgram) => {
        GRADE_ASSETS[hubProgram as keyof typeof GRADE_ASSETS].forEach(
          (assetNamePattern) => {
            userAssets = userAssets.filter(
              (userAsset) => !userAsset.name?.includes(assetNamePattern)
            )
          }
        )
      })
      const hubProgramsFromUri = hubProgramsFromUriStr.split(
        ','
      ) as HUB_PROGRAMS[]
      hubProgramsFromUri.forEach((hubProgramFromUri) => {
        if (GRADE_ASSETS[hubProgramFromUri as keyof typeof GRADE_ASSETS]) {
          ALL_GRADES.forEach((grade) => {
            if (
              hubProgramFromUri !== HUB_PROGRAMS.TEXAS_IMRA ||
              allowedSoundsAndSonidosGrades.includes(grade)
            ) {
              userAssets.push({
                businessKey:
                  `fake-asset-${hubProgramFromUri}-${grade}`.toLowerCase(),
                name: `${GRADE_ASSETS[hubProgramFromUri as keyof typeof GRADE_ASSETS][0]} ${grade}`,
                metadata: { grade },
              })
            }
          })
        }
      })
    }

    /* eslint-disable-next-line no-console */
    console.log(
      'injected test assets',
      userAssets.filter((userAsset) =>
        userAsset.businessKey.includes('fake-asset')
      )
    )
    return userAssets
  }

  return axios
    .get(url, config)
    .then(({ data }) => (isInternalEnv() ? injectTestAssets(data) : data))
}

export const getClassAssets = async () => {
  const token = AuthApi.getAccessToken()
  const url = `${EnvConstants.RETAIL_URL}/api/licensing/my/class/assets`
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }

  return axios.get(url, config).then(({ data }) => data)
}

export const getUserPrograms = async () => {
  const token = AuthApi.getAccessToken()
  const userBk = AuthApi.getUserBk()
  const url = `${EnvConstants.RETAIL_URL}/api/licensing/users/${userBk}/programs?expandAssets=true`
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }

  const injectTestPrograms = (userPrograms: UserProgram[]) => {
    const params = new URLSearchParams(window.location.search)
    const hubProgramsFromUriStr = params.get('programs')

    if (hubProgramsFromUriStr) {
      Object.keys(GRADE_PROGRAMS).forEach((hubProgram) => {
        GRADE_PROGRAMS[hubProgram as keyof typeof GRADE_PROGRAMS].forEach(
          (programNamePattern) => {
            userPrograms = userPrograms.filter(
              (userProgram) => !userProgram.name?.includes(programNamePattern)
            )
          }
        )
      })
      const hubProgramsFromUri = hubProgramsFromUriStr.split(
        ','
      ) as HUB_PROGRAMS[]
      hubProgramsFromUri.forEach((hubProgramFromUri) => {
        if (GRADE_PROGRAMS[hubProgramFromUri as keyof typeof GRADE_PROGRAMS]) {
          ALL_GRADES.forEach((grade) => {
            if (
              hubProgramFromUri !== HUB_PROGRAMS.TEXAS_IMRA ||
              allowedSoundsAndSonidosGrades.includes(grade)
            ) {
              userPrograms.push({
                businessKey:
                  `fake-program-${hubProgramFromUri}-${grade}`.toLowerCase(),
                name: `${GRADE_PROGRAMS[hubProgramFromUri as keyof typeof GRADE_PROGRAMS][0]} ${grade}`,
                contents: [],
              })
            }
          })
        }
      })
    }

    /* eslint-disable-next-line no-console */
    console.log(
      'injected test programs',
      userPrograms.filter((userProgram) =>
        userProgram.businessKey?.includes('fake-program')
      )
    )
    return userPrograms
  }

  return axios
    .get(url, config)
    .then(({ data }) => (isInternalEnv() ? injectTestPrograms(data) : data))
}

const hasAssetsOnContentLevel = (contents: Asset[][], assetsBks: string[]) =>
  contents.some((contentAssets) =>
    assetsBks.every((asset) =>
      EntitlementService.checkIfAssetIsPresentByBk(contentAssets, asset)
    )
  )

export const hasDEForCKLA = async () => {
  const { entitlement } = await getClassAssets()
  if (!entitlement) return false
  return hasAssetsOnContentLevel(collectAssetsFromEntitlements(entitlement), [
    BETA_ASSET_KEY,
    HUB_ASSET_BUSINESS_KEY,
  ])
}

// checks if a user has "Interactive Classroom (NEW FOR BTS 22)" and "CKLA" assets on the same user content level
export const hasICAndCKLAOnUserContentLevel = async () => {
  const programs = await getUserPrograms()

  return hasAssetsOnContentLevel(collectAssetsFromUserPrograms(programs), [
    INTERACTIVE_CLASSROOM_NEW_FOR_BTS_22_ASSET_KEY,
    HUB_ASSET_KEY,
  ])
}
